import * as Sentry from '@sentry/nextjs'
import { getConnectionInfo, getDevicePerformance } from './clientSpeed'

export interface SpeedReportingOptions {
  includeDeviceInfo?: boolean
  customTags?: Record<string, string>
  speedTestOptions?: {
    fileSizeKB?: number
    pingSamples?: number
  }
}

export async function reportClientSpeedToSentry(
  options: SpeedReportingOptions = {}
): Promise<void> {
  const { includeDeviceInfo = true, customTags = {} } = options

  try {
    // Get basic connection info (fast)
    const connectionInfo = getConnectionInfo()

    // Get device performance (fast)
    const deviceInfo = includeDeviceInfo ? getDevicePerformance() : null

    // Set basic connection tags
    if (connectionInfo) {
      Sentry.setTags({
        'network.effectiveType': connectionInfo.effectiveType,
        'network.downlink': connectionInfo.downlink.toString(),
        'network.rtt': connectionInfo.rtt.toString(),
        'network.saveData': connectionInfo.saveData.toString(),
        'network.type': connectionInfo.type || 'unknown',
        ...customTags,
      })

      // Set connection context
      Sentry.setContext('network', {
        effectiveType: connectionInfo.effectiveType,
        downlink: connectionInfo.downlink,
        rtt: connectionInfo.rtt,
        saveData: connectionInfo.saveData,
        type: connectionInfo.type,
        timestamp: new Date().toISOString(),
      })
    }

    // Set device performance tags
    if (deviceInfo) {
      Sentry.setTags({
        'device.cpuCores': deviceInfo.cpuCores.toString(),
        'device.memory': deviceInfo.memory.toString(),
        'device.performanceScore': deviceInfo.performanceScore.toString(),
      })

      // Set device context
      Sentry.setContext('device', {
        cpuCores: deviceInfo.cpuCores,
        memory: deviceInfo.memory,
        performanceScore: deviceInfo.performanceScore,
        timestamp: new Date().toISOString(),
      })
    }

    // Add a breadcrumb for speed reporting
    Sentry.addBreadcrumb({
      category: 'performance',
      message: 'Client speed information reported to Sentry',
      level: 'info',
      data: {
        includeDeviceInfo,
        connectionType: connectionInfo?.effectiveType,
        deviceScore: deviceInfo?.performanceScore,
      },
    })
  } catch (error) {
    console.error('Failed to report client speed to Sentry:', error)
    Sentry.captureException(error)
  }
}
